// src/lib/applications.ts: shared types, constants and helpers for the manager
// application / approval workflow.
import { supabase } from '../services/supabase';

export const C = {
  navy: '#0A1628', navyHover: '#111E35', gold: '#C9A84C', black: '#0A0A0A',
  white: '#FFFFFF', surface: '#F5F6F8', border: '#D0D5DD', borderLt: '#E8EBF0',
  text: '#0A0A0A', textSec: '#3D4552', muted: '#6B7280', mutedLt: '#9CA3AF',
  success: '#0A7C42', danger: '#C0222A', amber: '#92640A', teal: '#0D6E8A',
  blue: '#1D6FA4',
};

// ─────────────────────────────────────────────────────────────────────────────
// TODO: REPLACE BEFORE SENDING ANY CONTRACT OR INVOICE.
// These placeholders are rendered verbatim onto the generated contract and
// invoice PDFs. They are deliberately left as bracketed markers rather than
// invented details so an unfilled field is impossible to miss on the page.
// ─────────────────────────────────────────────────────────────────────────────
export const COMPANY = {
  name: '[COMPANY NAME]',
  addressLines: ['[ADDRESS LINE 1]', '[ADDRESS LINE 2]', '[CITY]', '[POSTCODE]', 'United Kingdom'],
  companyNumber: '[COMPANY NUMBER]',
  vatNumber: '[VAT NUMBER]',
  email: 'hello@altveno.com',
  bank: {
    name: '[BANK NAME]',
    accountName: '[ACCOUNT NAME]',
    sortCode: '[SORT CODE]',
    accountNumber: '[ACCOUNT NUMBER]',
    iban: '[IBAN]',
  },
};

export const JURISDICTIONS: { value: string; label: string; placeholder: string }[] = [
  { value: 'FCA',   label: 'United Kingdom (FCA)', placeholder: 'e.g. 123456' },
  { value: 'SEC',   label: 'United States (SEC)',  placeholder: 'e.g. 801-12345' },
  { value: 'MAS',   label: 'Singapore (MAS)',      placeholder: 'Enter your authorisation number' },
  { value: 'DIFC',  label: 'Dubai (DIFC)',         placeholder: 'Enter your authorisation number' },
  { value: 'ADGM',  label: 'Abu Dhabi (ADGM)',     placeholder: 'Enter your authorisation number' },
  { value: 'OTHER', label: 'Other',                placeholder: 'Enter your authorisation number' },
];

export const FUND_TYPES: { value: string; label: string }[] = [
  { value: 'hedge_fund',     label: 'Hedge Fund' },
  { value: 'pe',             label: 'Private Equity' },
  { value: 'vc',             label: 'Venture Capital' },
  { value: 're',             label: 'Real Estate' },
  { value: 'credit',         label: 'Credit' },
  { value: 'infrastructure', label: 'Infrastructure' },
  { value: 'multi_strategy', label: 'Multi-Strategy' },
];

export const AUM_RANGES = [
  'Pre-launch', 'Under $50m', '$50m-$100m', '$100m-$250m',
  '$250m-$500m', '$500m-$1bn', '$1bn-$5bn', 'Over $5bn',
];

export const TEAM_ROLES = [
  'Partner', 'Portfolio Manager', 'Analyst', 'Operations',
  'IR/Business Development', 'Compliance', 'Other',
];

export const HEARD_ABOUT = ['LinkedIn', 'Referral', 'Google Search', 'Industry Event', 'Other'];

export const MAX_TEAM_MEMBERS = 5; // includes the primary contact

// Pricing is GBP, the operating company is UK-based.
export const PRICING = {
  standard:       4150,
  emerging:       2075,
  additionalFund: 1660,
  maxUsers:       5,
};

export const gbp = (n: number) =>
  `£${Number(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

export interface ManagerApplication {
  id: string;
  contact_name: string;
  contact_email: string;
  contact_phone: string | null;
  heard_about: string | null;
  firm_name: string;
  firm_description: string | null;
  firm_website: string | null;
  firm_country: string | null;
  firm_city: string | null;
  jurisdiction: string;
  regulatory_number: string;
  regulatory_status: string | null;
  regulatory_data: any;
  regulator_firm_name: string | null;
  regulator_url: string | null;
  is_emerging_manager: boolean;
  previous_regulatory_number: string | null;
  previous_firm_name: string | null;
  fund_type: string | null;
  strategy: string | null;
  aum_range: string | null;
  fund_count: number | null;
  fund_description: string | null;
  status: string;
  admin_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  contract_type: string | null;
  contract_price: number | null;
  funds_included: number | null;
  additional_fund_price: number | null;
  max_users: number | null;
  special_terms: string | null;
  contract_start_date: string | null;
  invoice_number: string | null;
  contract_pdf_url: string | null;
  invoice_pdf_url: string | null;
  contract_sent_at: string | null;
  contract_signed_at: string | null;
  contract_signed_by_name: string | null;
  contract_signed_by_title: string | null;
  contract_signed_url: string | null;
  profile_id: string | null;
  firm_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface ApplicationTeamMember {
  id: string;
  application_id: string;
  name: string;
  email: string;
  role: string;
  is_primary_contact: boolean;
  invited_at: string | null;
  joined_at: string | null;
  created_at: string;
}

// Statuses that keep an applicant in the holding portal rather than the app.
// 'contract_signed' belongs here: the applicant has signed but an admin has not
// yet activated the account, so the portal is still where they should land.
export const PENDING_STATUSES = [
  'submitted', 'regulatory_check', 'admin_review', 'more_info_requested',
  'contract_sent', 'contract_signed',
];

export const APP_STATUS_LABELS: Record<string, string> = {
  submitted:            'Submitted',
  regulatory_check:     'Regulatory Check',
  admin_review:         'Admin Review',
  more_info_requested:  'More Info Requested',
  approved:             'Approved',
  contract_sent:        'Contract Sent',
  contract_signed:      'Contract Signed',
  declined:             'Declined',
};

export function appStatusColor(status: string): { fg: string; bg: string; bd: string } {
  switch (status) {
    case 'approved':
    case 'contract_signed':    return { fg: C.success, bg: '#F0FDF4', bd: '#BBF7D0' };
    case 'declined':           return { fg: C.danger,  bg: '#FEF2F2', bd: '#FECACA' };
    case 'more_info_requested':
    case 'contract_sent':      return { fg: C.amber,   bg: '#FFFBEB', bd: '#FDE68A' };
    case 'admin_review':       return { fg: C.blue,    bg: '#EFF6FF', bd: '#BFDBFE' };
    default:                   return { fg: C.muted,   bg: C.surface, bd: C.border };
  }
}

export const REG_STATUS_LABELS: Record<string, string> = {
  verified:      'Verified',
  partial:       'Partial Match',
  not_found:     'Not Found',
  manual_review: 'Manual Review',
  error:         'Check Unavailable',
  pending:       'Not Checked',
};

export function regStatusColor(status: string | null): { fg: string; bg: string; bd: string } {
  switch (status) {
    case 'verified':      return { fg: C.success, bg: '#F0FDF4', bd: '#BBF7D0' };
    case 'partial':       return { fg: C.amber,   bg: '#FFFBEB', bd: '#FDE68A' };
    case 'not_found':     return { fg: C.danger,  bg: '#FEF2F2', bd: '#FECACA' };
    case 'manual_review': return { fg: C.blue,    bg: '#EFF6FF', bd: '#BFDBFE' };
    default:              return { fg: C.muted,   bg: C.surface, bd: C.border };
  }
}

export const fundTypeLabel = (v: string | null) =>
  FUND_TYPES.find(f => f.value === v)?.label || v || '-';

export const jurisdictionLabel = (v: string | null) =>
  JURISDICTIONS.find(j => j.value === v)?.label || v || '-';

export function fmtDateTime(ts: string | null): string {
  if (!ts) return '-';
  return new Date(ts).toLocaleString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function fmtDate(ts: string | null): string {
  return ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '-';
}

// A password is required by signUp but never shown or reused. The applicant
// gets in via the magic link emailed to them.
export function generatePassword(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('').slice(0, 24) + 'Aa1!';
}

// Email sends must never take the workflow down with them.
export async function sendMail(type: string, data: Record<string, any>): Promise<void> {
  try {
    await supabase.functions.invoke('send-email-notification', { body: { type, data } });
  } catch (err) {
    console.error(`Email "${type}" failed:`, err);
  }
}

export async function nextInvoiceNumber(): Promise<string> {
  const { data, error } = await supabase.rpc('next_invoice_number');
  if (error || !data) {
    // Fall back to a timestamp-derived number so an approval is never blocked.
    return `ALT-${String(Date.now()).slice(-4)}`;
  }
  return data as string;
}
